/**
 * Employee Detail Page
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Typography,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  CheckCircle as CheckIcon,
  Block as BlockIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  PersonOff as PersonOffIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/common';
import {
  useGetEmployeeQuery,
  useDeleteEmployeeMutation,
  useActivateEmployeeMutation,
  useSuspendEmployeeMutation,
  useDeactivateEmployeeMutation,
  useResendEmployeeCredentialsMutation,
} from '../../store/api/rbacApi';
import { palette } from '../../theme/palette';
import {
  stripRetiredPermissionCodes,
  filterRetiredPermissionsFromList,
} from '../../utils/rbacRetiredPermissions';
import moment from 'moment';
import toast from 'react-hot-toast';

const EmployeeDetailPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const isDark = theme.palette.mode === 'dark';

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);

  const { data, isLoading, error } = useGetEmployeeQuery(parseInt(id || '0'), {
    skip: !id,
  });

  const [deleteEmployee, { isLoading: isDeleting }] = useDeleteEmployeeMutation();
  const [activateEmployee, { isLoading: isActivating }] = useActivateEmployeeMutation();
  const [suspendEmployee, { isLoading: isSuspending }] = useSuspendEmployeeMutation();
  const [deactivateEmployee, { isLoading: isDeactivating }] = useDeactivateEmployeeMutation();
  const [resendCredentials, { isLoading: isResending }] = useResendEmployeeCredentialsMutation();

  const handleActivate = async () => {
    if (!id) return;
    try {
      await activateEmployee(parseInt(id)).unwrap();
      toast.success(t('rbac.employees.toast.activateSuccessSimple'));
    } catch (error: any) {
      console.error('Failed to activate employee:', error);
      toast.error(error?.data?.message || t('rbac.employees.toast.activateError'));
    }
  };

  const handleSuspend = async () => {
    if (!id) return;
    try {
      await suspendEmployee(parseInt(id)).unwrap();
      toast.success(t('rbac.employees.toast.suspendSuccessSimple'));
    } catch (error: any) {
      console.error('Failed to suspend employee:', error);
      toast.error(error?.data?.message || t('rbac.employees.toast.suspendError'));
    }
  };

  const handleResendCredentials = async () => {
    if (!id) return;
    try {
      await resendCredentials(parseInt(id)).unwrap();
      toast.success(t('rbac.employees.toast.credentialsSuccessSimple'));
    } catch (error: any) {
      console.error('Failed to resend credentials:', error);
      toast.error(error?.data?.message || t('rbac.employees.toast.credentialsError'));
    }
  };

  const handleDeactivate = async () => {
    if (!id) return;
    try {
      await deactivateEmployee(parseInt(id)).unwrap();
      setDeactivateDialogOpen(false);
      toast.success(t('rbac.employees.toast.deactivateSuccessSimple'));
    } catch (error: any) {
      console.error('Failed to deactivate employee:', error);
      toast.error(error?.data?.message || t('rbac.employees.toast.deactivateError'));
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteEmployee(parseInt(id)).unwrap();
      setDeleteDialogOpen(false);
      toast.success(t('rbac.employees.toast.deleteSuccessSimple'));
      navigate('/employees');
    } catch (error: any) {
      console.error('Failed to delete employee:', error);
      toast.error(error?.data?.message || t('rbac.employees.toast.deleteError'));
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data?.data) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', py: 10 }}>
        <Typography color="error">{t('rbac.employees.detail.notFound')}</Typography>
        <Button onClick={() => navigate('/employees')} sx={{ mt: 2 }}>
          {t('rbac.employees.backToList')}
        </Button>
      </Box>
    );
  }

  const employee = data.data;
  const customPermissionsView = filterRetiredPermissionsFromList(employee.custom_permissions);
  const allPermissionsView = stripRetiredPermissionCodes(employee.all_permissions);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return { bg: palette.status.success + '20', color: palette.status.success };
      case 'suspended':
        return { bg: palette.status.warning + '20', color: palette.status.warning };
      case 'inactive':
        return { bg: '#E2E8F0', color: '#64748B' };
      default:
        return { bg: '#E2E8F0', color: '#64748B' };
    }
  };

  const statusColors = getStatusColor(employee.status);

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('rbac.employees.detail.title', { id: employee.employee_id })}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.employees'), path: '/employees' },
          { label: employee.user.full_name },
        ]}
      />

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/employees')}
        sx={{ mb: 3 }}
      >
        {t('rbac.employees.backToList')}
      </Button>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: '2fr 1fr',
          },
          gap: 3,
        }}
      >
        {/* Main Content */}
        <Box>
          <Card>
            <CardContent>
              {/* Header with Status */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>
                  {t('rbac.employees.detail.detailsHeading')}
                </Typography>
                <Chip
                  label={t(`rbac.status.${employee.status}`)}
                  sx={{
                    backgroundColor: statusColors.bg,
                    color: statusColors.color,
                    fontWeight: 600,
                  }}
                />
              </Box>

              {/* Warning Chips */}
              {(employee.must_change_password || !employee.can_access_dashboard) && (
                <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                  {employee.must_change_password && (
                    <Chip
                      label={t('rbac.employees.detail.mustChangePassword')}
                      color="warning"
                      size="small"
                    />
                  )}
                  {!employee.can_access_dashboard && (
                    <Chip
                      label={t('rbac.employees.detail.noDashboardAccess')}
                      color="error"
                      size="small"
                    />
                  )}
                </Box>
              )}

              {/* Personal Information */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                  },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('rbac.employees.detail.fullName')}</Typography>
                  <Typography variant="body1" fontWeight={600}>{employee.user.full_name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('rbac.employees.detail.employeeId')}</Typography>
                  <Typography variant="body1">{employee.employee_id}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('rbac.employees.detail.email')}</Typography>
                  <Typography variant="body1">{employee.user.email}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('rbac.employees.detail.phone')}</Typography>
                  <Typography variant="body1">{employee.user.phone_number}</Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Role & Department */}
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('rbac.employees.detail.roleDepartment')}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                  },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('rbac.employees.detail.role')}</Typography>
                  <Typography variant="body1">
                    <Chip
                      label={employee.role.name}
                      size="small"
                      sx={{
                        backgroundColor: palette.brand[50],
                        color: palette.brand[700],
                        fontWeight: 600,
                        mt: 0.5,
                      }}
                    />
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('rbac.employees.detail.department')}</Typography>
                  <Typography variant="body1">{employee.department || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('rbac.employees.detail.position')}</Typography>
                  <Typography variant="body1">{employee.position || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('rbac.employees.detail.hireDate')}</Typography>
                  <Typography variant="body1">
                    {moment(employee.hire_date).format('MMMM DD, YYYY')}
                  </Typography>
                </Box>
                {employee.termination_date && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('rbac.employees.detail.terminationDate')}</Typography>
                    <Typography variant="body1">
                      {moment(employee.termination_date).format('MMMM DD, YYYY')}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Account Information */}
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('rbac.employees.detail.accountInfo')}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                  },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('rbac.employees.detail.lastLogin')}</Typography>
                  <Typography variant="body1">
                    {employee.last_login_at
                      ? moment(employee.last_login_at).fromNow()
                      : t('rbac.employees.detail.never')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('rbac.employees.detail.passwordChanged')}</Typography>
                  <Typography variant="body1">
                    {employee.password_changed_at
                      ? moment(employee.password_changed_at).format('MMMM DD, YYYY')
                      : t('rbac.employees.detail.never')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('rbac.employees.detail.createdAt')}</Typography>
                  <Typography variant="body1">
                    {moment(employee.created_at).format('MMMM DD, YYYY HH:mm')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('rbac.employees.detail.lastUpdated')}</Typography>
                  <Typography variant="body1">
                    {moment(employee.updated_at).format('MMMM DD, YYYY HH:mm')}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {employee.use_only_custom_permissions && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('rbac.employees.detail.useOnlyCustomInfo')}
                </Alert>
              )}

              {/* Custom Permissions */}
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('rbac.employees.detail.customPermissions', { count: customPermissionsView.length })}
              </Typography>
              {customPermissionsView.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {customPermissionsView.map((permission) => (
                    <Chip
                      key={permission.id}
                      icon={<CheckIcon />}
                      label={permission.name}
                      size="small"
                      sx={{
                        backgroundColor: isDark ? palette.brandSecondary[800] : palette.brandSecondary[50],
                        color: isDark ? palette.brandSecondary[100] : palette.brandSecondary[700],
                        '& .MuiChip-icon': {
                          color: isDark ? palette.brandSecondary[200] : palette.brandSecondary[600],
                        },
                        transition: 'all 0.2s ease',
                        cursor: 'default',
                        '&:hover': {
                          backgroundColor: isDark ? palette.brandSecondary[700] : palette.brandSecondary[100],
                          transform: 'translateY(-2px)',
                          boxShadow: isDark 
                            ? '0 4px 12px rgba(25, 116, 187, 0.4)'
                            : '0 2px 8px rgba(25, 116, 187, 0.3)',
                        },
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('rbac.employees.detail.noCustomPermissions')}
                </Typography>
              )}

              <Divider sx={{ my: 3 }} />

              {/* All Permissions */}
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('rbac.employees.detail.allPermissions', { count: allPermissionsView.length })}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {employee.use_only_custom_permissions
                  ? t('rbac.employees.detail.allPermissionsNoteCustom')
                  : t('rbac.employees.detail.allPermissionsNoteUnion')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {allPermissionsView.map((permCode, index) => (
                  <Chip
                    key={index}
                    label={permCode}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: isDark ? palette.brand[600] : palette.brand[200],
                      color: isDark ? palette.brand[200] : palette.brand[600],
                      transition: 'all 0.2s ease',
                      cursor: 'default',
                      '&:hover': {
                        borderColor: isDark ? palette.brand[400] : palette.brand[400],
                        backgroundColor: isDark 
                          ? palette.brand[800] + '60'
                          : palette.brand[50] + '40',
                        color: isDark ? palette.brand[100] : palette.brand[700],
                        transform: 'translateY(-2px)',
                        boxShadow: isDark
                          ? '0 4px 12px rgba(59, 193, 207, 0.3)'
                          : '0 2px 8px rgba(59, 193, 207, 0.2)',
                      },
                    }}
                  />
                ))}
              </Box>

              {employee.notes && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {t('rbac.employees.detail.notes')}
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {employee.notes}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Sidebar */}
        <Box>
          {/* Actions Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('rbac.employees.detail.actions')}
              </Typography>
              <Button
                fullWidth
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/employees/${id}/edit`)}
                sx={{ mb: 2 }}
              >
                {t('rbac.employees.detail.editEmployee')}
              </Button>
              {employee.status !== 'active' ? (
                <Button
                  fullWidth
                  variant="outlined"
                  color="success"
                  startIcon={<CheckIcon />}
                  onClick={handleActivate}
                  disabled={isActivating}
                  sx={{ mb: 1 }}
                >
                  {isActivating ? t('rbac.employees.detail.activating') : t('rbac.employees.detail.activate')}
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="outlined"
                  color="warning"
                  startIcon={<BlockIcon />}
                  onClick={handleSuspend}
                  disabled={isSuspending}
                  sx={{ mb: 1 }}
                >
                  {isSuspending ? t('rbac.employees.detail.suspending') : t('rbac.employees.detail.suspend')}
                </Button>
              )}
              {(employee.status === 'active' || employee.status === 'suspended') && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="inherit"
                  startIcon={<PersonOffIcon />}
                  onClick={() => setDeactivateDialogOpen(true)}
                  disabled={isDeactivating}
                  sx={{ mb: 1 }}
                >
                  {isDeactivating ? t('rbac.employees.detail.deactivating') : t('rbac.employees.detail.deactivateEmployee')}
                </Button>
              )}
              <Button
                fullWidth
                variant="outlined"
                startIcon={<EmailIcon />}
                onClick={handleResendCredentials}
                disabled={isResending}
                sx={{ mb: 1 }}
              >
                {isResending ? t('rbac.employees.detail.sending') : t('rbac.employees.detail.resendCredentials')}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
              >
                {t('rbac.employees.detail.deleteEmployee')}
              </Button>
            </CardContent>
          </Card>

          {/* Role Summary Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('rbac.employees.detail.summary')}
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {employee.role.name}
              </Typography>
              {employee.role.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {employee.role.description}
                </Typography>
              )}
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {t('rbac.employees.detail.permissions', { count: allPermissionsView.length })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('rbac.employees.detail.custom', { count: customPermissionsView.length })}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!isDeleting) setDeleteDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('rbac.employees.detail.deleteTitle')}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning">
            {t('rbac.employees.detail.deleteConfirm', { name: employee.user.full_name })}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? t('rbac.employees.buttons.deleting') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deactivateDialogOpen}
        onClose={() => {
          if (!isDeactivating) setDeactivateDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('rbac.employees.detail.deactivateTitle')}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info">
            {t('rbac.employees.detail.deactivateConfirm', { name: employee.user.full_name })}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialogOpen(false)} disabled={isDeactivating}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeactivate}
            color="primary"
            variant="contained"
            disabled={isDeactivating}
          >
            {isDeactivating ? t('rbac.employees.buttons.deactivating') : t('rbac.employees.buttons.deactivate')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeDetailPage;
